import { useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBuilding,
  FaCheck,
  FaFileInvoice,
  FaEnvelope,
  FaLayerGroup,
  FaPenNib,
  FaShieldHalved,
  FaWandMagicSparkles,
} from "react-icons/fa6";
import { toast } from "react-toastify";
import Logo from "../components/Logo";
import { apiPostJson } from "../lib/api";

const benefits = [
  {
    icon: FaWandMagicSparkles,
    title: "From details to done in minutes",
    body: "Pick a client, add line items, and Invoice Studio handles GST, totals, words, and layout.",
  },
  {
    icon: FaLayerGroup,
    title: "Your business, already remembered",
    body: "Reuse sender profiles, bank accounts, signatures, and customer details without repetitive entry.",
  },
  {
    icon: FaBuilding,
    title: "One login, every organization",
    body: "Run multiple businesses from one account while keeping every invoice and profile neatly separated.",
  },
];

function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: "Custom features",
    message: "",
    website: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await apiPostJson("/api/contact", form);
      toast.success("Message sent. We’ll reply within one business day.");
      setForm((current) => ({ ...current, name: "", email: "", message: "", website: "" }));
    } catch (error: any) {
      toast.error(error?.message || "Unable to send your message");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="border-t border-slate-200 bg-[linear-gradient(145deg,#f0fdfa,#eff6ff)] py-24">
      <div className="mx-auto grid w-full max-w-6xl items-center gap-12 px-6 lg:grid-cols-[.85fr_1.15fr] lg:px-8">
        <div>
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-100 text-xl text-teal-700">
            <FaEnvelope />
          </span>
          <p className="mt-7 text-sm font-bold uppercase tracking-[0.18em] text-teal-700">
            Contact us
          </p>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
            Let’s build something useful.
          </h2>
          <p className="mt-5 max-w-lg text-lg leading-8 text-slate-600">
            Need a custom template, another tax regime, an integration, or help choosing
            the right deployment? Tell us what you have in mind.
          </p>
          <div className="mt-8 rounded-2xl border border-teal-100 bg-white/70 p-5 text-sm text-slate-600">
            <p className="font-semibold text-slate-800">A real reply, within one business day.</p>
            <a href="mailto:hello@getvak.com" className="mt-1 inline-block text-teal-700 hover:text-teal-800">
              hello@getvak.com
            </a>
          </div>
        </div>

        <form onSubmit={submit} className="grid gap-4 rounded-[2rem] border border-white bg-white/85 p-6 shadow-xl backdrop-blur transition duration-300 hover:-translate-y-1 hover:border-teal-200 hover:shadow-2xl hover:shadow-teal-900/10 sm:p-8">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1.5 text-sm font-semibold text-slate-600">
              Name
              <input className="input" required value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="Jane Doe" />
            </label>
            <label className="grid gap-1.5 text-sm font-semibold text-slate-600">
              Email
              <input className="input" type="email" required value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} placeholder="jane@company.com" />
            </label>
          </div>
          <label className="grid gap-1.5 text-sm font-semibold text-slate-600">
            What can we help with?
            <select className="input" value={form.topic} onChange={(event) => setForm((current) => ({ ...current, topic: event.target.value }))}>
              <option>Custom features</option>
              <option>One-time self-hosted package</option>
              <option>Managed deployment</option>
              <option>Custom invoice template</option>
              <option>General enquiry</option>
            </select>
          </label>
          <label className="grid gap-1.5 text-sm font-semibold text-slate-600">
            Message
            <textarea className="input min-h-32 resize-y" required maxLength={4000} value={form.message} onChange={(event) => setForm((current) => ({ ...current, message: event.target.value }))} placeholder="A short note about your workflow or requirements…" />
          </label>
          <label className="hidden" aria-hidden="true">
            Website
            <input tabIndex={-1} autoComplete="off" value={form.website} onChange={(event) => setForm((current) => ({ ...current, website: event.target.value }))} />
          </label>
          <button type="submit" className="button gap-2 py-3" disabled={submitting}>
            {submitting ? "Sending…" : "Send message"}
            {!submitting && <FaArrowRight />}
          </button>
        </form>
      </div>
    </section>
  );
}

export default function LandingPage() {
  return (
    <div className="landing-shell min-h-screen overflow-hidden text-ink">
      <header className="relative z-20 mx-auto flex w-full max-w-7xl items-center justify-between px-6 py-6 lg:px-8">
        <Link reloadDocument to="/" aria-label="Invoice Studio home">
          <Logo className="text-2xl text-slate-900" />
        </Link>
        <nav className="flex items-center gap-2 sm:gap-3">
          <Link
            reloadDocument
            to="/docs"
            className="hidden px-3 py-2 text-sm font-semibold text-slate-600 transition hover:text-teal-700 sm:inline-flex"
          >
            Docs
          </Link>
          <a
            href="#pricing"
            className="hidden px-3 py-2 text-sm font-semibold text-slate-600 transition hover:text-teal-700 sm:inline-flex"
          >
            Pricing
          </a>
          <a
            href="https://support.getvak.com"
            target="_blank"
            rel="noreferrer"
            className="hidden px-3 py-2 text-sm font-semibold text-slate-600 transition hover:text-teal-700 sm:inline-flex"
          >
            Support
          </a>
          <Link reloadDocument to="/login" className="button-secondary border-white/70 bg-white/60 backdrop-blur">
            Log in
          </Link>
          <Link reloadDocument to="/signup" className="button gap-2 px-5">
            Start free <FaArrowRight className="h-3.5 w-3.5" />
          </Link>
        </nav>
      </header>

      <main>
        <section className="relative mx-auto grid w-full max-w-7xl items-center gap-14 px-6 pb-24 pt-14 lg:grid-cols-[1.03fr_.97fr] lg:px-8 lg:pb-32 lg:pt-20">
          <div className="relative z-10">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white/70 px-3.5 py-2 text-xs font-bold uppercase tracking-[0.16em] text-teal-800 shadow-sm backdrop-blur">
              <FaFileInvoice /> GST-ready invoicing, simplified
            </div>
            <h1 className="max-w-3xl font-display text-5xl font-semibold leading-[1.02] tracking-[-0.04em] sm:text-6xl lg:text-7xl">
              Send invoices that make your business look{" "}
              <span className="landing-gradient-text">brilliant.</span>
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-8 text-slate-600 sm:text-xl">
              Create polished, accurate invoices without wrestling with spreadsheets,
              formatting, or repeated data entry. Your details stay ready for the next one.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link reloadDocument to="/signup" className="button gap-2 px-6 py-3.5 text-base shadow-xl shadow-teal-900/15">
                Create your first invoice <FaArrowRight className="h-4 w-4" />
              </Link>
              <Link reloadDocument to="/login" className="button-secondary bg-white/70 px-6 py-3.5 text-base backdrop-blur">
                Sign in to your workspace
              </Link>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-slate-600">
              {["No card required", "Set up in minutes", "Built for Indian businesses"].map((item) => (
                <span key={item} className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-teal-100 text-teal-700">
                    <FaCheck className="h-2.5 w-2.5" />
                  </span>
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-xl lg:max-w-none">
            <div className="landing-orb landing-orb-one" />
            <div className="landing-orb landing-orb-two" />
            <div className="relative rotate-[1.5deg] rounded-[2rem] border border-white/80 bg-white/60 p-3 shadow-[0_35px_100px_-30px_rgba(15,118,110,.45)] backdrop-blur-xl">
              <div className="overflow-hidden rounded-[1.45rem] border border-slate-200/80 bg-white">
                <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-teal-700">Invoice</p>
                    <p className="mt-1 font-display text-xl font-semibold">INV-0108</p>
                  </div>
                  <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700">
                    Ready to send
                  </span>
                </div>
                <div className="grid gap-7 p-6 sm:p-8">
                  <div className="flex justify-between gap-6">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400">Billed to</p>
                      <p className="mt-2 font-semibold">Northstar Creative Labs</p>
                      <p className="mt-1 text-xs leading-5 text-slate-500">Bengaluru, Karnataka<br />GSTIN 29ABCDE1234F1Z5</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs uppercase tracking-wider text-slate-400">Issue date</p>
                      <p className="mt-2 text-sm font-semibold">27 June 2026</p>
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-slate-100">
                    <div className="grid grid-cols-[1fr_auto] bg-slate-50 px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">
                      <span>Description</span><span>Amount</span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] px-4 py-4 text-sm">
                      <span className="font-medium">Brand identity & design system</span><span className="font-semibold">₹48,000.00</span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] border-t border-slate-100 px-4 py-4 text-sm">
                      <span className="font-medium">Web experience consultation</span><span className="font-semibold">₹22,000.00</span>
                    </div>
                  </div>
                  <div className="ml-auto grid w-56 gap-2 text-sm">
                    <div className="flex justify-between text-slate-500"><span>Subtotal</span><span>₹70,000.00</span></div>
                    <div className="flex justify-between text-slate-500"><span>GST (18%)</span><span>₹12,600.00</span></div>
                    <div className="mt-2 flex justify-between border-t border-slate-200 pt-3 text-base font-bold"><span>Total</span><span className="text-teal-700">₹82,600.00</span></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-7 -left-3 flex items-center gap-3 rounded-2xl border border-white bg-white/90 p-4 shadow-xl backdrop-blur sm:-left-8">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-100 text-teal-700"><FaCheck /></span>
              <div><p className="text-sm font-bold">PDF generated</p><p className="text-xs text-slate-500">Clean, branded, and ready</p></div>
            </div>
          </div>
        </section>

        <section className="border-y border-teal-100/80 bg-[linear-gradient(135deg,#ecfdf5_0%,#ecfeff_48%,#eff6ff_100%)] py-24">
          <div className="mx-auto w-full max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-teal-700">Everything in its place</p>
              <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">Less admin. More momentum.</h2>
              <p className="mt-5 text-lg leading-8 text-slate-600">A calm workspace for the details behind every professional invoice.</p>
            </div>
            <div className="mt-14 grid gap-6 md:grid-cols-3">
              {benefits.map(({ icon: Icon, title, body }) => (
                <article key={title} className="group rounded-3xl border border-slate-200/80 bg-white p-7 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-xl text-teal-700 transition group-hover:bg-teal-700 group-hover:text-white"><Icon /></span>
                  <h3 className="mt-6 font-display text-xl font-semibold">{title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className="relative bg-slate-950 py-24 text-white">
          <div className="mx-auto w-full max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-teal-700">
                Simple, honest pricing
              </p>
              <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
                Own it today. Let us run it later.
              </h2>
              <p className="mt-5 text-lg leading-8 text-slate-300">
                Start with the complete product, choose managed hosting, or commission
                precisely the custom workflow your business needs.
              </p>
            </div>

            <div className="mt-14 grid items-stretch gap-6 lg:grid-cols-3">
              <article className="relative order-2 flex flex-col overflow-hidden rounded-[2rem] border border-teal-600 bg-slate-950 text-white shadow-2xl shadow-teal-900/15 transition duration-300 hover:-translate-y-1 hover:border-teal-300 hover:shadow-teal-500/25">
                <div className="relative z-10 flex items-center justify-center gap-2 bg-gradient-to-r from-amber-400 to-amber-500 px-5 py-3 text-center text-xs font-extrabold uppercase tracking-wide text-slate-950 sm:text-sm">
                  <span aria-hidden="true">●</span>
                  <span>Lifetime deal · first 100 users only</span>
                  <span aria-hidden="true">●</span>
                </div>
                <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-teal-400/20 blur-3xl" />
                <div className="absolute -bottom-24 -left-20 h-56 w-56 rounded-full bg-cyan-400/15 blur-3xl" />
                <div className="relative flex flex-1 flex-col p-7 sm:p-8">
                  <div className="flex items-center justify-between">
                    <p className="font-display text-xl font-semibold">One-time</p>
                    <span className="rounded-full bg-teal-400/15 px-3 py-1 text-xs font-bold text-teal-300">
                      Available now
                    </span>
                  </div>
                  <div className="mt-7 flex items-end gap-3">
                    <span className="font-display text-5xl font-semibold tracking-tight">$199</span>
                    <span className="pb-1 font-display text-2xl font-semibold text-slate-500 line-through">$499</span>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-slate-300">
                    Whole package. Buy once, self-hosted, yours forever.
                  </p>
                  <ul className="mt-7 grid gap-3 text-sm text-slate-200">
                  {[
                    "Lifetime license, yours to keep",
                    "Self-host on your infrastructure",
                    "Unlimited invoices and companies",
                    "Onboarding and email support",
                  ].map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <FaCheck className="mt-1 h-3.5 w-3.5 flex-none text-teal-300" />
                      {feature}
                    </li>
                  ))}
                  </ul>
                  <a
                    href="#contact"
                    className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-bold text-slate-950 transition hover:bg-teal-50"
                  >
                    Buy package <FaArrowRight />
                  </a>
                </div>
              </article>

              <article className="order-3 flex flex-col rounded-[2rem] border border-slate-700 bg-slate-900 p-7 text-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-violet-400 hover:shadow-xl hover:shadow-violet-900/30 sm:p-8">
                <div className="flex items-center justify-between">
                  <p className="font-display text-xl font-semibold">Custom features</p>
                  <span className="rounded-full bg-violet-400/15 px-3 py-1 text-xs font-bold text-violet-300">
                    Add-ons
                  </span>
                </div>
                <div className="mt-7 font-display text-5xl font-semibold tracking-tight">
                  Custom
                </div>
                <p className="mt-3 text-sm leading-6 text-slate-300">
                  For bespoke templates, special integrations, and workflow changes.
                </p>
                <ul className="mt-7 grid gap-3 text-sm text-slate-200">
                  {[
                    "Custom invoice templates and branding",
                    "New tax regimes or currencies",
                    "Integrations and data import",
                    "Dedicated implementation support",
                  ].map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <FaCheck className="mt-1 h-3.5 w-3.5 flex-none text-violet-600" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className="mt-8 inline-flex w-full items-center justify-center rounded-xl bg-white px-4 py-3 text-sm font-bold text-slate-950 transition hover:bg-violet-50"
                >
                  Request quote
                </a>
              </article>

              <article className="order-1 flex flex-col rounded-[2rem] border border-slate-700 bg-slate-900 p-7 text-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-teal-400 hover:shadow-xl hover:shadow-teal-900/30 sm:p-8">
                <div className="flex items-center justify-between">
                  <p className="font-display text-xl font-semibold">Managed</p>
                  <span className="rounded-full bg-emerald-400/15 px-3 py-1 text-xs font-bold text-emerald-300">
                    Available now
                  </span>
                </div>
                <div className="mt-7 flex items-end gap-2">
                  <span className="font-display text-5xl font-semibold tracking-tight">$10</span>
                  <span className="pb-1 text-sm text-slate-400">/ month</span>
                </div>
                <p className="mt-3 text-sm leading-6 text-slate-300">
                  Monthly. We host, scale, and maintain Invoice Studio.
                </p>
                <ul className="mt-7 grid gap-3 text-sm text-slate-200">
                  {[
                    "Truly unlimited organizations, users and invoices",
                    "We deploy, host and scale it",
                    "Monitoring and automatic updates",
                    "Priority support",
                  ].map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <FaCheck className="mt-1 h-3.5 w-3.5 flex-none text-teal-600" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  reloadDocument
                  to="/signup"
                  className="mt-8 inline-flex w-full items-center justify-center rounded-xl bg-white px-4 py-3 text-sm font-bold text-slate-950 transition hover:bg-teal-50"
                >
                  Try now <FaArrowRight />
                </Link>
              </article>
            </div>

            <p className="mt-7 text-center text-sm text-slate-400">
              Special features, integrations, and bespoke templates can be quoted separately.
            </p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-6 py-24 lg:px-8">
          <div className="grid items-start gap-14 lg:grid-cols-[.78fr_1.22fr]">
            <div className="lg:sticky lg:top-10">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-teal-700">
                Invoicing without the ritual
              </p>
              <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
                Three steps. One polished invoice.
              </h2>
              <p className="mt-5 max-w-lg text-lg leading-8 text-slate-600">
                The parts you reuse stay saved. The parts that change are quick to enter.
                Everything else is handled for you.
              </p>
                <Link
                  reloadDocument
                  to="/signup"
                className="mt-8 inline-flex items-center gap-2 font-bold text-teal-700 transition hover:gap-3"
              >
                Try the workflow <FaArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <div className="grid gap-4">
              {[
                {
                  number: "01",
                  title: "Set up your business once",
                  body: "Add your sender details, GST information, bank account, signature, and brand preferences.",
                },
                {
                  number: "02",
                  title: "Choose a client and add the work",
                  body: "Select a saved company, enter your line items, and let totals and GST calculate instantly.",
                },
                {
                  number: "03",
                  title: "Review, generate, and send",
                  body: "Preview the finished invoice, create a crisp PDF, and keep it ready in your documents.",
                },
              ].map((step) => (
                <article
                  key={step.number}
                  className="group grid gap-5 rounded-3xl border border-slate-200/80 bg-white/80 p-6 shadow-sm transition hover:border-teal-200 hover:shadow-lg sm:grid-cols-[4.5rem_1fr] sm:p-8"
                >
                  <span className="font-display text-4xl font-semibold text-teal-200 transition group-hover:text-teal-500">
                    {step.number}
                  </span>
                  <div>
                    <h3 className="font-display text-xl font-semibold">{step.title}</h3>
                    <p className="mt-2 leading-7 text-slate-600">{step.body}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-slate-200/70 bg-slate-900 py-24 text-white">
          <div className="mx-auto grid w-full max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:px-8">
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-slate-800 p-6 shadow-2xl sm:p-8">
              <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-teal-400/15 blur-3xl" />
              <div className="relative grid grid-cols-2 gap-4">
                {[
                  ["GST", "Calculated automatically"],
                  ["PDF", "Ready in one click"],
                  ["Profiles", "Saved and reusable"],
                  ["Organizations", "Cleanly separated"],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="group min-h-36 rounded-2xl border border-white/10 bg-white/[.06] p-5 backdrop-blur transition duration-300 hover:-translate-y-1 hover:border-teal-300/60 hover:bg-white/[.1] hover:shadow-lg hover:shadow-teal-950/30"
                  >
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-300">
                      {label}
                    </p>
                    <p className="mt-5 font-display text-lg font-semibold leading-6">{value}</p>
                    <span className="mt-5 flex h-7 w-7 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300 transition group-hover:scale-110 group-hover:bg-emerald-400/25">
                      <FaCheck className="h-3 w-3" />
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-400/15 text-xl text-teal-300">
                <FaPenNib />
              </div>
              <p className="mt-7 text-sm font-bold uppercase tracking-[0.18em] text-teal-300">
                Built for real business
              </p>
              <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
                The control you need, minus the complexity.
              </h2>
              <p className="mt-5 text-lg leading-8 text-slate-300">
                Accurate calculations, consistent presentation, and properly separated
                workspaces give you confidence every time an invoice leaves your desk.
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {[
                  "GST-ready calculations",
                  "Totals written in words",
                  "Branded sender profiles",
                  "Private organization data",
                  "Saved customer records",
                  "Searchable PDF archive",
                ].map((item) => (
                  <span key={item} className="flex items-center gap-3 text-sm font-semibold text-slate-200">
                    <FaCheck className="h-3.5 w-3.5 text-teal-300" />
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-6 py-24 lg:px-8">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-950 px-7 py-16 text-center text-white shadow-2xl sm:px-16">
            <div className="landing-cta-glow" />
            <FaShieldHalved className="relative mx-auto h-8 w-8 text-teal-300" />
            <h2 className="relative mt-6 font-display text-4xl font-semibold tracking-tight sm:text-5xl">Your next invoice can be the easy one.</h2>
            <p className="relative mx-auto mt-5 max-w-2xl text-lg leading-8 text-slate-300">Start with one organization, save your business details once, and turn invoicing into a two-minute task.</p>
            <Link reloadDocument to="/signup" className="relative mt-8 inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3.5 font-bold text-slate-950 transition hover:bg-teal-50">
              Start creating for free <FaArrowRight />
            </Link>
          </div>
        </section>

        <ContactSection />
      </main>

      <footer className="border-t border-slate-200 bg-white/70">
        <div className="mx-auto grid w-full max-w-7xl gap-10 px-6 py-14 sm:grid-cols-[1.5fr_1fr_1fr] lg:px-8">
          <div>
            <Logo className="text-xl text-slate-900" />
            <p className="mt-4 max-w-sm text-sm leading-6 text-slate-500">
              Beautiful, GST-ready invoicing for founders, freelancers, and small teams.
              A focused product by GetVAK.
            </p>
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">Invoice Studio</h3>
            <div className="mt-4 grid gap-3 text-sm text-slate-500">
              <a href="#pricing" className="transition hover:text-teal-700">Pricing</a>
              <Link reloadDocument to="/docs" className="transition hover:text-teal-700">Documentation</Link>
              <Link reloadDocument to="/login" className="transition hover:text-teal-700">Log in</Link>
              <Link reloadDocument to="/signup" className="transition hover:text-teal-700">Create account</Link>
              <a href="https://getvak.com/products/invoice/" target="_blank" rel="noreferrer" className="transition hover:text-teal-700">
                Product details
              </a>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">GetVAK</h3>
            <div className="mt-4 grid gap-3 text-sm text-slate-500">
              <a href="https://getvak.com/" target="_blank" rel="noreferrer" className="transition hover:text-teal-700">Main website</a>
              <a href="https://getvak.com/terms" target="_blank" rel="noreferrer" className="transition hover:text-teal-700">Terms of Use</a>
              <a href="https://getvak.com/privacy" target="_blank" rel="noreferrer" className="transition hover:text-teal-700">Privacy Policy</a>
              <a href="https://getvak.com/license" target="_blank" rel="noreferrer" className="transition hover:text-teal-700">License</a>
            </div>
          </div>
        </div>
        <div className="border-t border-slate-200">
          <div className="mx-auto flex w-full max-w-7xl flex-col justify-between gap-2 px-6 py-6 text-xs text-slate-400 sm:flex-row lg:px-8">
            <span>© {new Date().getFullYear()} Invoice Studio. A GetVAK product.</span>
            <span>Premium products. Shipped with commitment.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
